/**
 * PSR Phase 2 — news aggregator (GDELT tone rollup for locality + energy keywords).
 */
import { fetchGdeltDocument } from '../dataProviders/gdeltRequest';
import { factToCitationRef } from '../../lib/sentiment/citationRefs';
import { resolveSentimentSiteContext } from './siteContext';
import {
  emptySentimentResult,
  type SentimentFetchOptions,
  type SentimentNormalizedFact,
  type SentimentFetchResult,
  type SentimentFetcher,
} from './types';
import { probeHttpAvailability } from './probeAvailability';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

const GDELT_BASE = 'https://api.gdeltproject.org/api/v2/doc/doc';
const DEFAULT_MAX = 25;

interface GdeltArticle {
  url?: string;
  title?: string;
  tone?: number | string;
  seendate?: string;
  domain?: string;
}

export const newsAggregatorFetcher: SentimentFetcher = {
  sourceId: 'news-aggregator',

  async probeAvailability() {
    // R-130-10 — GDELT sends NO CORS headers, so a direct browser probe is
    // blocked and surfaces as "Failed to fetch" (a TypeError, not an HTTP
    // status). Route through `/api/proxy` (api.gdeltproject.org is allowlisted
    // there) so the probe reflects real upstream health.
    return probeHttpAvailability({
      url: `${GDELT_BASE}?query=${encodeURIComponent('energy')}&mode=ArtList&format=json&maxrecords=1&sort=datedesc`,
      unconfiguredStatuses: [401, 403],
      proxied: true,
    }); // R-130-10
  },

  async isAvailable(siteId, opts) {
    const ctx = await resolveSentimentSiteContext(siteId, opts);
    return !!(ctx?.locality || ctx?.state);
  },

  async fetch(siteId, opts?: SentimentFetchOptions): Promise<SentimentFetchResult> {
    throwIfAborted(opts?.signal);
    try {
      const ctx = await resolveSentimentSiteContext(siteId, opts);
      if (!ctx) {
        return emptySentimentResult('news-aggregator', ['news-aggregator: no site context']);
      }
      const place = [ctx.locality, ctx.state].filter(Boolean).join(' ');
      const terms = opts?.technologyTerms?.length ? opts.technologyTerms : ['data center'];
      const query = `(${terms.join(' OR ')}) ${place}`.trim();
      const max = Math.min(opts?.perSourceCap ?? DEFAULT_MAX, 50);
      const url = `${GDELT_BASE}?query=${encodeURIComponent(query)}&mode=ArtList&format=json&maxrecords=${max}&sort=hybridrel&timespan=${Math.max(30, Math.min(1825, opts?.trendWindowDays ?? 365))}d`;

      const res = await fetchGdeltDocument(url, 15_000, opts?.signal);
      throwIfAborted(opts?.signal);
      if (!res.ok) {
        return emptySentimentResult('news-aggregator', [`news-aggregator: GDELT HTTP ${res.status}`]);
      }

      // GDELT returns a PLAIN-TEXT body (e.g. the "limit requests to one every
      // 5 seconds" rate-limit notice) even on a 200 with `format=json`, so
      // `res.json()` can throw on a parse error. Read as text first and parse
      // defensively — a non-JSON body means "unavailable", never a thrown error.
      const raw = await res.text();
      let json: { articles?: GdeltArticle[] };
      try {
        json = JSON.parse(raw) as { articles?: GdeltArticle[] };
      } catch {
        return emptySentimentResult('news-aggregator', ['news-aggregator: GDELT returned a non-JSON body (rate-limited or throttled)']);
      }
      const articles = json.articles ?? [];
      if (articles.length === 0) {
        return emptySentimentResult('news-aggregator', ['news-aggregator: no articles in trend window']);
      }

      const tones: number[] = [];
      for (const a of articles) {
        const t = typeof a.tone === 'number' ? a.tone : parseFloat(String(a.tone ?? ''));
        if (Number.isFinite(t)) tones.push(t);
      }
      const meanTone = tones.length
        ? tones.reduce((s, v) => s + v, 0) / tones.length
        : 0;
      const normalizedTone = Math.max(-1, Math.min(1, meanTone / 10));

      const now = new Date().toISOString();
      const rollupFact = {
        key: 'gdelt_mean_tone',
        label: 'Local news mean tone (GDELT)',
        value: Math.round(normalizedTone * 1000) / 1000,
        units: 'polarity -1..1',
        url: 'https://www.gdeltproject.org/',
        observedAt: now,
      };
      const countFact = {
        key: 'gdelt_article_count',
        label: 'Articles sampled',
        value: articles.length,
        units: 'articles',
        url: 'https://www.gdeltproject.org/',
        observedAt: now,
      };

      const topHeadline = articles.find(a => a.title && a.url);
      const facts: SentimentNormalizedFact[] = [...(tones.length ? [rollupFact] : []), countFact];
      if (topHeadline?.title && topHeadline.url) {
        facts.push({
          key: 'gdelt_top_headline',
          label: 'Top headline',
          value: topHeadline.title.slice(0, 200),
          url: topHeadline.url,
          observedAt: topHeadline.seendate ?? now,
        });
      }

      return {
        sourceId: 'news-aggregator',
        coverage: articles.length >= 5 ? 'full' : 'partial',
        normalizedFacts: facts,
        citationRefs: facts.map((f, i) => factToCitationRef(f, 'news-aggregator', i + 1)),
        errors: tones.length ? [] : ['Article tone was unavailable; article count is coverage context, not neutral sentiment.'],
        fetchedAt: now,
      };
    } catch (e) {
      rethrowIfAborted(opts?.signal, e);
      return emptySentimentResult('news-aggregator', [`news-aggregator: ${e instanceof Error ? e.message : String(e)}`]);
    }
  },
};
